import React from "react";
import PropTypes from "prop-types";
import { defaultTemplate } from "core/hoc";
import { Content } from "core/layouts";
import { observer } from "mobx-react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { BaasicButton, BaasicDropdown, BasicInput, BaasicFieldDropdown, NumericInputField } from 'core/components';
import { uuid, handleKeyDown, handlePaste } from "core/utils";
import { DropDownButton, DropDownButtonItem, Button, ChipList } from "@progress/kendo-react-buttons";
import { GlobalItemChangeListener } from 'core/components';
import { FacilityOrderTemplateNewFlowCustomOrderingDoctorAdd } from "application/facility-order-new-flow/components";

const FacilityOrderTemplateNewFlowClinicalEditTemplate = function ({ facilityOrderTemplateNewFlowClinicalEditViewStore, t, disabled, disabledSendOrder }) {
	const {
		contentLoading,
		facilityOrderTemplate,
		selectedStatusAbrv,
		isQtyChange
	} = facilityOrderTemplateNewFlowClinicalEditViewStore;

	return (
		facilityOrderTemplate ?
			<Content loading={contentLoading} >
				<GlobalItemChangeListener isChanged={isQtyChange} />
				{selectedStatusAbrv === "discharge" ?
					<FacilityOrderTemplateDischargesTable
						key={facilityOrderTemplate.id}
						facilityOrderTemplate={facilityOrderTemplate}
						facilityOrderTemplateNewFlowClinicalEditViewStore={facilityOrderTemplateNewFlowClinicalEditViewStore}
						disabled={disabled}
						disabledSendOrder={disabledSendOrder}
					/>
					:
					<FacilityOrderTemplateTable
						key={facilityOrderTemplate.id}
						facilityOrderTemplate={facilityOrderTemplate}
						facilityOrderTemplateNewFlowClinicalEditViewStore={facilityOrderTemplateNewFlowClinicalEditViewStore}
						disabled={disabled}
						disabledSendOrder={disabledSendOrder}
						t={t}
					/>
				}
			</Content>
			:
			<React.Fragment></React.Fragment>
	);
};

const FacilityOrderTemplateTable = observer(({ facilityOrderTemplate, facilityOrderTemplateNewFlowClinicalEditViewStore, disabled, disabledSendOrder, t }) => {
	const {
		form,
		clinicalStatusStore,
		doctorStore,
		missingDocumentStore,
		getSelectedInactiveOption,
		changeItemsVisibility,
		reviewTemplate,
		setImportAction,
		openFacilityOrderTemplateItemNewFlowCalculationModal,
		openFacilityOrderTemplateMissingDocumentNoteModal,
		productStore,
		saveItemOnProductSelect,
		editItem,
		updateItem,
		addEmptyItem,
		updateFacilityOrderTempalte,
		generateStandardWrittenOrder,
		generateMedicalNecessityForm,
		setImportActionToDeleteAndAddItem,
		deleteFacilityOrderTemplateItem,
		openFacilityOrderTemplateNewFlowUSIntakePortalNoteModal,
		openFacilityOrderTemplateDoNotSupplyModal,
		openFacilityOrderTemplateNewFlowUSIntakeMissingProductNoteModal,
		updatePatient,
		setTemplateStatus,
		isEligible,
		disabledItem,
		dxCode1Store,
		dxCode2Store,
		dxCode3Store,
		dxCode4Store,
		dxCode5Store,
		dxCode6Store,
		dxCode7Store,
		dxCode8Store,
		syncOrderingDoctors,
		isSyncInProgress,
		generateMedicalNecessityFormAndStandardWrittenOrder,
		openFacilityOrderTemplateNewFlowUSIntakeClinicalStatusNoteModal,
		setIncludeInDocReqEmailData,
		showDocReqEmailDataNotification,
		openPatientMissingDocsPreview,
		isMasterPatient,
		facilityOrderPatient,
		patient,
		missingDocsPreviousValue,
		openFacilityOrderTemplateNewFlowDeleteModal,
		importFacilityOrderTemplate,
		isImportAllowed,
		showItemQuantities,
		templateExpanded,
		showThirdRow,
		toggleThirdRow,
		openFacilityOrderTemplateNewFlowInvoiceNoteModal,
		sendAgoraPo,
		deleteImportActionId,
		setItemToggle,
		insertImportActionId
	} = facilityOrderTemplateNewFlowClinicalEditViewStore;

	const productDropdownItemRender = (li, itemProps) => {
		const itemName = itemProps && itemProps.dataItem.description;
		const isPreferredIcon = itemProps && itemProps.dataItem.isPreferred && facilityOrderTemplate.itemCategory.abrv === "ostomy" ?
			<i className="u-icon u-icon--xsml u-icon--star-outlined u-mar--right--tny"></i> : null;
		const isBackorderedIcon = itemProps && itemProps.dataItem.backordered ? <i className="u-icon u-icon--sml u-icon--backordered u-mar--left--tny"></i> : null;
		const nonBillableIcon = itemProps && !itemProps.dataItem.billableToMedicare ? <i className="u-icon u-icon--sml u-icon--non-billable u-mar--left--tny"></i> : null;
		const itemChildren = (
			<span style={{ display: "flex", flexDirection: "column", width: "100%", }}>
				<span style={{ flex: "1" }}> {li.props.children} </span>
				<span style={{ fontSize: "12px" }}> {isPreferredIcon} {itemName} {nonBillableIcon} {isBackorderedIcon}</span>
			</span>
		);
		return React.cloneElement(li, li.props, itemChildren);
	}

	const tagRender = (tagData, li) => React.cloneElement(li, { children: <span key={uuid()} onClick={() => openFacilityOrderTemplateMissingDocumentNoteModal(tagData.data[0])}>{tagData.data[0].name}</span> });

	const dxCodeDropdownItemRender = (li, itemProps) => {
		const itemName = itemProps && itemProps.dataItem.name;
		const itemChildren = (
			<span style={{ display: "flex", flexDirection: "column", width: "100%", }}>
				<span style={{ flex: "1" }}> {li.props.children} </span>
				<span style={{ fontSize: "12px" }}>{itemName} </span>
			</span>
		);
		return React.cloneElement(li, li.props, itemChildren);
	}

	const handleDropDownItemsOnClick = async (e) => {
		if (e.item.text === "No changes") {
			setTemplateStatus("no-change");
		}
		else if (e.item.text === "Change/Review") {
			setTemplateStatus("change");
		}
		else if (e.item.text === "Discontinued") {
			setTemplateStatus("discharge");
		}
	}

	const includeInDocReqEmailData = [
		{
			text: "Yes",
			value: true,
		},
		{
			text: "No",
			value: false,
		}
	];

	const isExpanded = () => {
		return isMasterPatient || (facilityOrderTemplate.id === templateExpanded.id && templateExpanded.expanded === true);
	}

	const tableColSpan = () => {
		if ((isExpanded() || facilityOrderTemplate.isDifferentQty) && facilityOrderTemplate.facilityOrderTemplateItems.length > 0) {
			return 7;
		}
		return 6;
	}

	const handleItemClick = (e) => {
		e.currentTarget.select();
		showItemQuantities(facilityOrderTemplate, true);
	};

	const onTargetFocus = (e) => {
		e.target.select();
	}
	const dropdownRef = React.createRef();

	return (
		<div className={`table--secondary__wrapper u-mar--bottom--med--condition ${getSelectedInactiveOption()} ${disabled || disabledSendOrder || disabledItem || !facilityOrderTemplate.itemsVisible || (facilityOrderTemplate.facilityOrderTemplateItems && facilityOrderTemplate.facilityOrderTemplateItems.some(x => !x.productId)) ? 'is-add-new-disabled' : ''}`}>
			<ReactTooltip positionStrategy="fixed" id={"registerTemplateTip" + facilityOrderTemplate.id} place="bottom" />
			<table className="table--secondary w--100">
				<colgroup>
					<col width="400" />
					<col width="150" />
					<col width="80" />
					{(isExpanded() || facilityOrderTemplate.isDifferentQty) && facilityOrderTemplate.facilityOrderTemplateItems.length > 0 && <col width={72} />}
					<col width="70" />
					<col width="" />
					<col width="70" />
				</colgroup>
				<thead>
					<tr>
						<th colSpan={tableColSpan()} className="type--left">
							<div className="btn--group__wrapper u-flex--space-between">
								<label>{facilityOrderTemplate.subCategory.name.toUpperCase()}</label>
								{facilityOrderTemplate.notCurrentTube != null && !facilityOrderTemplate.inactiveReasonId && facilityOrderTemplate.facilityOrderTemplateItems.some(x => x.product && x.importActionId != deleteImportActionId && x.orderQuantity) &&
									<div className="u-mar--left--xsml u-mar--bottom--tny">
										{facilityOrderTemplate.notCurrentTube ? <i className="u-icon u-icon--med u-icon--calendar-x--dark" /> : <i className="u-icon u-icon--med u-icon--calendar-yes--dark" />}
									</div>
								}
								{
									facilityOrderTemplate.missingProduct &&
									<Button className="btn btn--status--red u-mar--left--xsml" icon="u-icon u-icon--med-lrg u-icon--missing-product" onClick={() => openFacilityOrderTemplateNewFlowUSIntakeMissingProductNoteModal()} disabled={disabled} />
								}
								{
									<div className="card--tertiary__header__actions u-align--h--right">
										<div className="btn--group" key={facilityOrderTemplate.id}>
											{!sendAgoraPo && isMasterPatient && <Button className="btn no-hover" icon="u-icon u-icon--med u-icon--us-invoice-note" onClick={() => openFacilityOrderTemplateNewFlowInvoiceNoteModal()} disabled={disabled || disabledSendOrder} data-tooltip-content="Invoice Note" data-tooltip-id={"registerTemplateTip" + facilityOrderTemplate.id} />}
											<div className="btn--group status--sqare__wrapper" data-tooltip-content="View Note" data-tooltip-id={(disabled || !facilityOrderTemplate.portalNote) ? ("registerTemplateTip" + facilityOrderTemplate.id) : ""}>
												<Button className="btn no-hover" icon={"u-icon u-icon--med u-icon--us-history"} onClick={() => { openFacilityOrderTemplateNewFlowUSIntakePortalNoteModal() }} disabled={(disabled || disabledSendOrder || !facilityOrderTemplate.portalNote)} data-tooltip-content="View Note" data-tooltip-id={"registerTemplateTip" + facilityOrderTemplate.id} />
											</div>
											{!facilityOrderTemplate.missingProduct && <Button className="btn no-hover" icon="u-icon u-icon--med u-icon--us-question-note" onClick={() => { openFacilityOrderTemplateNewFlowUSIntakeMissingProductNoteModal() }} disabled={disabled} data-tooltip-content="Missing Product" data-tooltip-id={"registerTemplateTip" + facilityOrderTemplate.id} />}
											{facilityOrderTemplate.inactiveReasonId !== 16 &&
												<div className="btn--group status--sqare__wrapper" data-tooltip-content={`${facilityOrderTemplate.inactiveReasonId == 16 ? "Unmark" : "Mark"} As Do Not Supply`} data-tooltip-id={(disabled || disabledSendOrder || (facilityOrderTemplate.isDoNotSupply && facilityOrderTemplate.inactiveReasonId === 16)) ? ("registerTemplateTip" + facilityOrderTemplate.id) : ""}>
													<Button className="btn no-hover" icon="u-icon u-icon--med u-icon--do-not-supply" onClick={() => { openFacilityOrderTemplateDoNotSupplyModal() }} disabled={disabled || disabledSendOrder || (facilityOrderTemplate.isDoNotSupply && facilityOrderTemplate.inactiveReasonId === 16)} data-tooltip-content={`${facilityOrderTemplate.inactiveReasonId == 16 ? "Unmark" : "Mark"} As Do Not Supply`} data-tooltip-id={"registerTemplateTip" + facilityOrderTemplate.id} />
												</div>}
											{facilityOrderTemplate.templateId && <Button className="btn no-hover" icon="u-icon u-icon--med u-icon--danger--primary" onClick={() => setTemplateStatus("discharge", 58)} disabled={disabled || disabledSendOrder} data-tooltip-content="Discontinued" data-tooltip-id={"registerTemplateTip" + facilityOrderTemplate.id} />}
											{!facilityOrderTemplate.templateId && <Button className="btn no-hover" icon="u-icon u-icon--med u-icon--delete--brand" onClick={() => openFacilityOrderTemplateNewFlowDeleteModal()} disabled={disabled || disabledSendOrder} data-tooltip-content="Delete template" data-tooltip-id={"registerTemplateTip" + facilityOrderTemplate.id} />}
											<div className="btn--group status--sqare__wrapper" data-tooltip-content="This template can't be imported" data-tooltip-id={(disabled || !facilityOrderTemplate.isClinicalReviewed || facilityOrderTemplate.importDetailStatus.abrv !== 'new') ? ("registerTemplateTip" + facilityOrderTemplate.id) : ""}>
												{isImportAllowed && <Button className="btn no-hover" icon="u-icon u-icon--sml u-icon--upload" onClick={() => importFacilityOrderTemplate()} disabled={disabled || !facilityOrderTemplate.isClinicalReviewed || facilityOrderTemplate.importDetailStatus.abrv !== 'new' || facilityOrderTemplate.inactiveReasonId === 17 || facilityOrderTemplate.inactiveReasonId === 18} data-tooltip-content={"Import to Brightree"} data-tooltip-id={"registerTemplateTip" + facilityOrderTemplate.id} />}
											</div>

											<div className="btn--group__divider"></div>
										</div>
										{isEligible && facilityOrderTemplate.clinicalStatus && facilityOrderTemplate.clinicalStatus.name != "Covered" && facilityOrderTemplate.clinicalStatus.name != "Prior Auth Obtained" && facilityOrderTemplate.clinicalStatus.name != "Does Not Meet Insurance Criteria" &&
											<Button className="btn btn--link" icon="u-icon u-icon--med u-icon--us-note" onClick={() => openFacilityOrderTemplateNewFlowUSIntakeClinicalStatusNoteModal()} disabled={disabled || disabledSendOrder || form.submitting || form.$('clinicalStatusId').disabled} data-tooltip-content="Clinical Status Note" data-tooltip-id={"registerTemplateTip" + facilityOrderTemplate.id} />
										}
										{isEligible &&
											<div className="is-blue">
												<BaasicFieldDropdown className="k-dropdown--primary k-dropdown--nowrap" field={form.$('clinicalStatusId')} store={clinicalStatusStore} disabled={disabled || disabledSendOrder} showChangeFlag={facilityOrderTemplate && facilityOrderTemplate.templateId && facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.clinicalStatusId != facilityOrderTemplate.facilityOrderTemplateInitial.clinicalStatusId} previousValue={facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.facilityOrderTemplateInitial.clinicalStatus && facilityOrderTemplate.facilityOrderTemplateInitial.clinicalStatus.name} />
											</div>
										}
										{facilityOrderTemplate.inactiveReasonId == 16 &&
											<div className={`card--tertiary__status--card is-dns ${(!facilityOrderTemplate.templateId || facilityOrderTemplate.newFlowStatus.abrv === "new-order") ? "is-new-order" : facilityOrderTemplate.newFlowStatus.abrv === "change" ? "is-changes" : facilityOrderTemplate.newFlowStatus.abrv === "in-progress" ? "is-not-assigned" : "is-no-changes"} ${disabled || disabledSendOrder || facilityOrderTemplate.isDoNotSupply ? "is-disabled" : ""}`} >
												<div className="status--sqare__holder dns">
													<Button icon="u-icon u-icon--xsml u-icon--slash" className={`btn btn--base btn--ghost k-dropdown-button--icons status--sqare btn--${facilityOrderTemplate.isDoNotSupply ? "dns" : "dns-opaque"}`} onClick={() => openFacilityOrderTemplateDoNotSupplyModal()} disabled={disabled || disabledSendOrder || facilityOrderTemplate.isDoNotSupply}>Do Not Supply</Button>
												</div>
											</div>
										}
										{((!facilityOrderTemplate.templateId || (facilityOrderTemplate.newFlowStatus.abrv === "new-order" && (facilityOrderTemplate.importDetailStatus.abrv === 'imported' || facilityOrderTemplate.importDetailStatus.abrv === 'failed'))) && (facilityOrderTemplate.inactiveReasonId != 15 && facilityOrderTemplate.inactiveReasonId != 17 && facilityOrderTemplate.inactiveReasonId != 18)) ?
											<div className={`card--tertiary__status--card ${disabled || disabledSendOrder ? "is-disabled" : ""}`}>
												<div className="status--sqare__holder yellow">
													<div className="status--sqare__wrapper">
														<Button className="btn status--square btn--yellow" icon="u-icon u-icon--xsml u-icon--box--yellow" onClick={() => { }} disabled={disabled || disabledSendOrder} >NEW ORDER</Button>
													</div>
												</div>
											</div>
											:
											!((facilityOrderTemplate.newFlowUSIntakeStatus && facilityOrderTemplate.newFlowUSIntakeStatus.abrv === "in-progress") ||
												(facilityOrderTemplate.newFlowUSIntakeStatus && facilityOrderTemplate.newFlowUSIntakeStatus.abrv === "intake-reviewed" && facilityOrderTemplate.newFlowStatus && facilityOrderTemplate.newFlowStatus.abrv === "in-progress")) &&
											<div className={`card--tertiary__status--card ${disabled || disabledSendOrder ? "is-disabled" : ""}`}>
												<div className={`status--sqare__holder ${facilityOrderTemplate.newFlowStatus.abrv === "no-change" ? "success" : facilityOrderTemplate.newFlowStatus.abrv === "change" ? "warning" : facilityOrderTemplate.newFlowStatus.abrv === "discharge" ? "danger" : ""}`}>
													<div className="status--sqare__wrapper">
														<DropDownButton
															text={`${facilityOrderTemplate.newFlowStatus.abrv === "no-change" ? "NO CHANGES" : facilityOrderTemplate.newFlowStatus.abrv === "change" ? "CHANGES" : facilityOrderTemplate.newFlowStatus.abrv === "discharge" ? "DISCONTINUED" : ""}`}
															fillMode={"solid"}
															themeColor={"primary"}
															onItemClick={(e) => handleDropDownItemsOnClick(e)}
															buttonClass={`btn btn--base btn--ghost k-dropdown-button--icons status--sqare ${facilityOrderTemplate.newFlowStatus.abrv === "no-change" ? "success" : facilityOrderTemplate.newFlowStatus.abrv === "change" ? "warning" : facilityOrderTemplate.newFlowStatus.abrv === "discharge" ? "danger" : ""}`}
															disabled={disabled || disabledSendOrder}
															popupSettings={{
																animate: false,
																anchorAlign: { horizontal: "right", vertical: "bottom" },
																popupAlign: { horizontal: "right", vertical: "top" },
																popupClass: 'is-clean'
															}}
														>
															{facilityOrderTemplate.newFlowStatus.abrv !== "discharge" && <DropDownButtonItem iconClass="u-icon u-icon--xmed u-icon--danger" text="Discontinued" />}
														</DropDownButton>
													</div>
												</div>
											</div>
										}

										<div className="status--sqare__wrapper 555">
											{
												facilityOrderTemplate.isClinicalReviewed ?
													<Button className="btn btn--icon btn--icon--sml-med btn--primary" icon="u-icon u-icon--med u-icon--unreview" onClick={() => { reviewTemplate() }} disabled={disabled || disabledSendOrder || (!isEligible)} data-tooltip-content="Unreview" data-tooltip-id={"registerTemplateTip" + facilityOrderTemplate.id} />
													:
													<div className="status--sqare__wrapper" data-tooltip-content={(isEligible && !facilityOrderTemplate.clinicalStatusId) ? "Clinical Status must be selected" : (showDocReqEmailDataNotification() && facilityOrderTemplate.pastFacilityOrderTemplate && facilityOrderTemplate.pastFacilityOrderTemplate.includeInDocReqEmail == null) ? "\"Include the past order’s missing docs in DR email\" must be selected" : "Review"} data-tooltip-id={"registerTemplateTip" + facilityOrderTemplate.id}>
														<Button className="btn btn--icon btn--icon--sml-med btn--primary" icon="u-icon u-icon--med u-icon--check-list" onClick={() => { reviewTemplate() }} disabled={disabled || disabledSendOrder || disabledItem || (isEligible && !facilityOrderTemplate.clinicalStatusId) || (showDocReqEmailDataNotification() && facilityOrderTemplate.pastFacilityOrderTemplate && facilityOrderTemplate.pastFacilityOrderTemplate.includeInDocReqEmail == null)} data-tooltip-content={(isEligible && !facilityOrderTemplate.clinicalStatusId) ? "Clinical Status must be selected" : "Review"} data-tooltip-id={"registerTemplateTip" + facilityOrderTemplate.id} />
													</div>
											}
										</div>
									</div>
								}
							</div>

							<div className="modal__body u-mar--top--sml is-blue">
								{showDocReqEmailDataNotification() &&
									<div className="row">
										<div className="form__group col col-lrg-12">
											<div className="message message--sml-med message--note">
												<div className="u-flex--default w--100">
													<i className="u-icon u-icon--med u-icon--warning-circle--peach u-mar--right--sml"></i>
													<div className="u-mar--right--tny u-flex--grow--1 u-flex--default">
														{facilityOrderTemplate.pastFacilityOrderTemplate ? "Include the past order’s missing docs in DR email?" : "You may want to request documents for the past order, but the system cannot find the past template."}
														<a className="u-mar--left--sml" target='_blank' onClick={() => openPatientMissingDocsPreview()}><i className="u-icon u-icon--expand u-icon--sml"></i></a>
													</div>
													{facilityOrderTemplate.pastFacilityOrderTemplate &&
														<div className="u-align--h--right type--whitespace--nowrap">
															<ChipList
																className="btn__group--preselect"
																disabled={disabled || disabledSendOrder || disabledItem}
																selection="single"
																data={includeInDocReqEmailData}
																value={facilityOrderTemplate.pastFacilityOrderTemplate.includeInDocReqEmail}
																onChange={(e) => { setIncludeInDocReqEmailData(e) }}
															/>
														</div>
													}
												</div>
											</div>
										</div>
									</div>
								}
								<div className="row">
									<div className="form__group col col-lrg-7">
										<BasicInput field={form.$('note')} onBlur={() => { updateFacilityOrderTempalte('note') }} disabled={disabled || disabledSendOrder} showChangeFlag={facilityOrderTemplate && facilityOrderTemplate.templateId && facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.note != facilityOrderTemplate.facilityOrderTemplateInitial.note} previousValue={facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.facilityOrderTemplateInitial.note} />
									</div>
									<div className="form__group col col-lrg-5">
										<BasicInput field={form.$('swo')} onBlur={() => { updateFacilityOrderTempalte('swo') }} showChangeFlag={facilityOrderTemplate && facilityOrderTemplate.templateId && facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.swo != facilityOrderTemplate.facilityOrderTemplateInitial.swo} disabled={disabled || disabledSendOrder} previousValue={facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.facilityOrderTemplateInitial.swo} />
									</div>
								</div>
								<div className="row">
									<div className="form__group col col-lrg-10">
										<BaasicFieldDropdown field={form.$('facilityOrderTemplateMissingDocuments')} store={missingDocumentStore} multi={true} tagRender={tagRender} autoClose={true} disabled={disabled} showChangeFlag={facilityOrderTemplate && facilityOrderTemplate.templateId && missingDocsPreviousValue != null} previousValue={missingDocsPreviousValue != null && missingDocsPreviousValue} />
									</div>
									<div className="form__group col col-lrg-2">
										<div className="form__group__label">Generate Documents:</div>
										<div className='u-flex--default btn--grouped'>
											<BaasicButton
												onlyIcon={true}
												className='btn btn--sml btn--light t u-mar--right--tny'
												label='SWO & PN'
												icon="u-icon u-icon--sml u-icon--download--brand"
												onClick={() => generateMedicalNecessityFormAndStandardWrittenOrder()}
											/>
											<BaasicButton
												className='btn btn--sml btn--light u-mar--right--tny'
												label='SWO'
												onClick={() => generateStandardWrittenOrder()}
											/>
											<BaasicButton
												className='btn btn--sml btn--light u-mar--right--tny'
												label='PN'
												onClick={() => generateMedicalNecessityForm()}
											/>
											<FacilityOrderTemplateNewFlowCustomOrderingDoctorAdd facilityOrderTemplate={facilityOrderTemplate} />
										</div>
									</div>
								</div>
								<a className={`u-anchor--sml--underline u-mar--bottom--med`} onClick={() => toggleThirdRow()}> {showThirdRow ? "Show less" : "Show all"}</a>
								{showThirdRow &&
									<div className="row">
										<div className="col col-lrg-4">
											<div className="row">
												<div className="col col-lrg-2">
													<NumericInputField field={form.$('height')} setDefaultType={true} showSpinners={false} onClick={onTargetFocus} onBlur={() => { updatePatient('height') }} disabled={disabled || disabledSendOrder} showChangeFlag={facilityOrderTemplate && facilityOrderTemplate.templateId && facilityOrderPatient && facilityOrderPatient.facilityOrderPatientInitial && patient.height != facilityOrderPatient.facilityOrderPatientInitial.height} previousValue={facilityOrderPatient && facilityOrderPatient.facilityOrderPatientInitial && (facilityOrderPatient.facilityOrderPatientInitial.height != null ? facilityOrderPatient.facilityOrderPatientInitial.height : '')} />
												</div>
												<div className="col col-lrg-2">
													<NumericInputField field={form.$('weight')} setDefaultType={true} showSpinners={false} onClick={onTargetFocus} onBlur={() => { updatePatient('weight') }} disabled={disabled || disabledSendOrder} showChangeFlag={facilityOrderTemplate && facilityOrderTemplate.templateId && facilityOrderPatient && facilityOrderPatient.facilityOrderPatientInitial && patient.weight != facilityOrderPatient.facilityOrderPatientInitial.weight} previousValue={facilityOrderPatient && facilityOrderPatient.facilityOrderPatientInitial && (facilityOrderPatient.facilityOrderPatientInitial.weight != null ? facilityOrderPatient.facilityOrderPatientInitial.weight : '')} />
												</div>
												<div className="form__group col col-lrg-8">
													<div className="form__group__label">
														{t(form.$('orderingDoctorId').label)}
														<i title='Sync Ordering Doctors' className={`u-icon u-icon--sml u-icon--synchronize--${isSyncInProgress ? "grey" : "primary"} u-mar--left--tny`} onClick={() => syncOrderingDoctors()}></i>
													</div>
													<BaasicFieldDropdown field={form.$('orderingDoctorId')} store={doctorStore} allowEmpty={true} disabled={disabled || disabledSendOrder} labelDisplayed={false} showChangeFlag={facilityOrderTemplate && facilityOrderTemplate.templateId && facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.orderingDoctorId != facilityOrderTemplate.facilityOrderTemplateInitial.orderingDoctorId} previousValue={facilityOrderTemplate.facilityOrderTemplateInitial && (facilityOrderTemplate.facilityOrderTemplateInitial.orderingDoctor ? facilityOrderTemplate.facilityOrderTemplateInitial.orderingDoctor.name : '')} />
												</div>
											</div>
										</div>
										<div className="col col-lrg-1">
											<BaasicFieldDropdown field={form.$('dxCode1')} store={dxCode1Store} allowEmpty={true} itemRender={dxCodeDropdownItemRender} disabled={disabled || disabledSendOrder} showChangeFlag={facilityOrderTemplate && facilityOrderTemplate.templateId && facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.dxCode1 != facilityOrderTemplate.facilityOrderTemplateInitial.dxCode1} previousValue={facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.facilityOrderTemplateInitial.dxCode1} />
										</div>
										<div className="col col-lrg-1">
											<BaasicFieldDropdown field={form.$('dxCode2')} store={dxCode2Store} allowEmpty={true} itemRender={dxCodeDropdownItemRender} disabled={disabled || disabledSendOrder} showChangeFlag={facilityOrderTemplate && facilityOrderTemplate.templateId && facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.dxCode2 != facilityOrderTemplate.facilityOrderTemplateInitial.dxCode2} previousValue={facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.facilityOrderTemplateInitial.dxCode2} />
										</div>
										<div className="col col-lrg-1">
											<BaasicFieldDropdown field={form.$('dxCode3')} store={dxCode3Store} allowEmpty={true} itemRender={dxCodeDropdownItemRender} disabled={disabled || disabledSendOrder} showChangeFlag={facilityOrderTemplate && facilityOrderTemplate.templateId && facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.dxCode3 != facilityOrderTemplate.facilityOrderTemplateInitial.dxCode3} previousValue={facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.facilityOrderTemplateInitial.dxCode3} />
										</div>
										<div className="col col-lrg-1">
											<BaasicFieldDropdown field={form.$('dxCode4')} store={dxCode4Store} allowEmpty={true} itemRender={dxCodeDropdownItemRender} disabled={disabled || disabledSendOrder} showChangeFlag={facilityOrderTemplate && facilityOrderTemplate.templateId && facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.dxCode4 != facilityOrderTemplate.facilityOrderTemplateInitial.dxCode4} previousValue={facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.facilityOrderTemplateInitial.dxCode4} />
										</div>
										<div className="col col-lrg-1">
											<BaasicFieldDropdown field={form.$('dxCode5')} store={dxCode5Store} allowEmpty={true} itemRender={dxCodeDropdownItemRender} disabled={disabled || disabledSendOrder} showChangeFlag={facilityOrderTemplate && facilityOrderTemplate.templateId && facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.dxCode5 != facilityOrderTemplate.facilityOrderTemplateInitial.dxCode5} previousValue={facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.facilityOrderTemplateInitial.dxCode5} />
										</div>
										<div className="col col-lrg-1">
											<BaasicFieldDropdown field={form.$('dxCode6')} store={dxCode6Store} allowEmpty={true} itemRender={dxCodeDropdownItemRender} disabled={disabled || disabledSendOrder} showChangeFlag={facilityOrderTemplate && facilityOrderTemplate.templateId && facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.dxCode6 != facilityOrderTemplate.facilityOrderTemplateInitial.dxCode6} previousValue={facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.facilityOrderTemplateInitial.dxCode6} />
										</div>
										<div className="col col-lrg-1">
											<BaasicFieldDropdown field={form.$('dxCode7')} store={dxCode7Store} allowEmpty={true} itemRender={dxCodeDropdownItemRender} disabled={disabled || disabledSendOrder} showChangeFlag={facilityOrderTemplate && facilityOrderTemplate.templateId && facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.dxCode7 != facilityOrderTemplate.facilityOrderTemplateInitial.dxCode7} previousValue={facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.facilityOrderTemplateInitial.dxCode7} />
										</div>
										<div className="col col-lrg-1">
											<BaasicFieldDropdown field={form.$('dxCode8')} store={dxCode8Store} allowEmpty={true} itemRender={dxCodeDropdownItemRender} disabled={disabled || disabledSendOrder} showChangeFlag={facilityOrderTemplate && facilityOrderTemplate.templateId && facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.dxCode8 != facilityOrderTemplate.facilityOrderTemplateInitial.dxCode8} previousValue={facilityOrderTemplate.facilityOrderTemplateInitial && facilityOrderTemplate.facilityOrderTemplateInitial.dxCode8} />
										</div>
									</div>
								}
							</div>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td colSpan={3} className="table--secondary__last card--tertiary__body u-cursor--pointer no-right" onClick={() => { changeItemsVisibility() }}>
							<div className="">
								<div className="u-flex u-flex--default">
									<span className="type--tiny type--color--opaque"> {facilityOrderTemplate.itemsVisible ? "Hide Items" : "See Items"}</span>
									<i className={"u-icon u-icon--xsml " + (facilityOrderTemplate.itemsVisible ? "u-icon--arrow-up" : "u-icon--arrow-down") + " u-mar--left--sml"} />
								</div>
							</div>
						</td>
						<td colSpan={1} className="table--secondary--special table__label--special u-cursor--pointer" onClick={() => { changeItemsVisibility() }}>
							{(isExpanded() || facilityOrderTemplate.isDifferentQty) && facilityOrderTemplate.facilityOrderTemplateItems.length > 0 &&
								<div>
									{facilityOrderTemplate.itemsVisible ? "TEMP QTY" : ""}
								</div>
							}
						</td>
						{(isExpanded() || facilityOrderTemplate.isDifferentQty) && facilityOrderTemplate.facilityOrderTemplateItems.length > 0 &&
							<td colSpan={1} className="table--secondary--special table__label--special u-cursor--pointer" onClick={() => { changeItemsVisibility() }}>
								{facilityOrderTemplate.itemsVisible ? "SO QTY" : ""}
							</td>
						}
						<td colSpan={2} className="table--secondary__last card--tertiary__body u-cursor--pointer" onClick={() => { changeItemsVisibility() }}>
						</td>
					</tr>
					{facilityOrderTemplate.itemsVisible && facilityOrderTemplate.facilityOrderTemplateItems.map((item, index) => {
						var isFormula = (item && item.product && item.product.itemType && (item.product.itemType.abrv === 'bottle' || item.product.itemType.abrv === 'can')) || (item.product && item.product.procedureCode_ && item.product.procedureCode_.abrv === "B4155");
						if (!item.product && item.toggle) {
							setTimeout(() => {
								if (dropdownRef.current) {
									// v14 dropped the inner .k-dropdown-wrap — the DropDownList root element is the clickable toggle
									const toggle = dropdownRef.current.element;
									if (toggle) {
										setItemToggle(item);
										toggle.click();
									}
								}
							}, 0);
						}
						return (
							<tr key={item.id} className={`${(facilityOrderTemplate.importActionId != insertImportActionId && item.importActionId === insertImportActionId) ? "new-row" : (item.importAction && item.importAction.abrv === "delete" ? "deleted-row" : "")} ${!item.product ? "is-focused" : ""}`}>
								<td className="is-flex">
									{
										item.product ? <React.Fragment>{item.product.description} <span className="table__icons-wrapper u-mar--left--auto"> {!item.product.billableToMedicare && <i className="u-icon u-icon--sml u-icon--non-billable"></i>} {(item.backordered != null ? item.backordered : item.product.backordered) && <i className="u-icon u-icon--med-lrg u-icon--backordered"></i>}</span></React.Fragment> :
											<BaasicDropdown
												placeholder="Select item name..."
												store={productStore}
												value={item.product}
												className="input--table"
												itemRender={productDropdownItemRender}
												onChange={(e) => { saveItemOnProductSelect(item, index, e.target.value) }}
												disabled={disabled || disabledSendOrder || disabledItem}
												customRef={dropdownRef}
											/>
									}
								</td>
								<td>
									{item.product && item.product.itemId}
								</td>
								<td>
									{item.procedureCode && item.procedureCode}
								</td>
								<td className="table-col-input text-align-left has-input" id={"templateQuantity-" + facilityOrderTemplate.id}>
									<ReactTooltip positionStrategy="fixed" id={"registerTipItemChanged" + item.id} place="top" />
									{item.isTemplateQuantityChanged && <div className="table-col-input__notif" data-tooltip-id={"registerTipItemChanged" + item.id} data-tooltip-content={"Previous: " + (item.facilityOrderTemplateItemInitial && item.facilityOrderTemplateItemInitial.templateQuantity ? item.facilityOrderTemplateItemInitial.templateQuantity : "blank")}></div>}
									{item.product &&
										<input
											className="input--table input--inherit-color no-arrows input--text-align--left w--70--px"
											onClick={(e) => handleItemClick(e)}
											onKeyDown={handleKeyDown}
											onPaste={handlePaste}
											onChange={(e) => { editItem(item, parseInt(e.target.value), 'templateQuantity') }}
											onBlur={() => { updateItem(item) }}
											value={item.templateQuantity}
											type="number"
											min="0"
											disabled={disabled || disabledSendOrder || disabledItem || isMasterPatient}
										/>
									}
									{(item.sendSeparatelyQty | 0) > 0 && <i className="u-icon u-icon--ss u-icon--sml table__icon--ss" data-tooltip-id={"registerTipItemChanged" + item.id} data-tooltip-content={item.sendSeparatelyQty} />}
								</td>
								{(isExpanded() || facilityOrderTemplate.isDifferentQty) &&
									<td className="table-col-input text-align-left has-input" id={"orderQuantity-" + facilityOrderTemplate.id}>
										{item.isOrderQuantityChanged &&
											<div>
												<ReactTooltip positionStrategy="fixed" id={"registerTipItemChangedOrder" + item.id} place="top" />
												<div
													className="table-col-input__notif"
													data-tooltip-id={"registerTipItemChangedOrder" + item.id}
													data-tooltip-content={"Previous: " + (item.facilityOrderTemplateItemInitial && item.facilityOrderTemplateItemInitial.orderQuantity ? item.facilityOrderTemplateItemInitial.orderQuantity : "blank")}>
												</div>
											</div>
										}
										{item.product &&
											<input
												className="input--table input--inherit-color no-arrows input--text-align--left w--70--px"
												onKeyDown={handleKeyDown}
												onPaste={handlePaste}
												onClick={(e) => handleItemClick(e)}
												onChange={async (e) => { await editItem(item, parseInt(e.target.value), 'orderQuantity') }}
												onBlur={() => { updateItem(item) }}
												value={item.orderQuantity}
												type="number"
												min="0"
												disabled={disabled || disabledSendOrder || disabledItem}
											/>
										}
									</td>
								}
								<td className="table-col-input">
									<ReactTooltip positionStrategy="fixed" id={"registerTipItemChangedNote" + item.id} place="top" />
									{item.isNoteChanged && <div className="table-col-input__notif" data-tooltip-id={"registerTipItemChangedNote" + item.id} data-tooltip-content={"Previous: " + (item.facilityOrderTemplateItemInitial && item.facilityOrderTemplateItemInitial.note ? item.facilityOrderTemplateItemInitial.note : "blank")}></div>}
									{!isMasterPatient && isFormula ?
										<input
											className="input--table input--inherit-color no-arrows input--text-align--left"
											onClick={() => openFacilityOrderTemplateItemNewFlowCalculationModal(facilityOrderTemplate.facilityOrderTemplateItems, index)}
											value={item.note}
											defaultValue={item.note}
											disabled={disabled || disabledSendOrder || disabledItem}
											readOnly={true}
										/> :
										<input
											className="input--table input--inherit-color no-arrows input--text-align--left"
											onClick={isFormula ? () => openFacilityOrderTemplateItemNewFlowCalculationModal(facilityOrderTemplate.facilityOrderTemplateItems, index) : (e) => e.target.select()}
											onChange={(e) => { item.note = e.target.value }}
											onBlur={() => { updateItem(item, true) }}
											value={item.note}
											disabled={disabled || disabledSendOrder || disabledItem || !item.product}
											readOnly={isFormula}
										/>
									}
								</td>
								<td className="type--center is-hidden">
									<div className="table__icons">
										{item.importActionId && (item.importActionId !== insertImportActionId) && (
												<Button
													disabled={disabled || disabledSendOrder || disabledItem}
													className="btn btn--icon"
													icon="u-icon u-icon--update-table u-icon--sml"
													label="Change"
													onlyIcon={true}
													title={"Swap"}
													onClick={() => setImportActionToDeleteAndAddItem(item, index)}
												></Button>
											)}
										{item.importAction && item.importAction.abrv === "delete" ?
											<Button
												className="btn btn--icon is-delete"
												icon="u-icon u-icon--sml u-icon--undo--table"
												label="Undelete"
												onlyIcon={true}
												onClick={() => setImportAction(item, "update", true)}
												disabled={disabled || disabledSendOrder || disabledItem}
												title={"Undo"}
											></Button>
											:
											<Button
												className="btn btn--icon is-delete"
												icon={`u-icon u-icon--sml ${!item.product ? "u-icon--close--table" : "u-icon--delete--primary"}`}
												label="Delete"
												onlyIcon={true}
												onClick={() => { deleteFacilityOrderTemplateItem(index, true) }}
												disabled={disabled || disabledSendOrder || disabledItem}
												title={!item.product ? "Cancel" : "Delete"}
											></Button>
										}
									</div>
								</td>
							</tr>
						);
					})
					}
				</tbody>
			</table>
			{facilityOrderTemplate.itemsVisible && !(facilityOrderTemplate.facilityOrderTemplateItems && facilityOrderTemplate.facilityOrderTemplateItems.some(x => !x.productId)) &&
				(<button className={`table--secondary__add-button${(disabled || disabledSendOrder || disabledItem) ? ' is-disabled' : ''}`} onClick={() => { addEmptyItem() }} disabled={disabled || disabledSendOrder || disabledItem}>
					<span></span>
				</button>)}
		</div >
	);
});

const FacilityOrderTemplateDischargesTable = observer(({ facilityOrderTemplate, facilityOrderTemplateNewFlowClinicalEditViewStore, disabled, disabledSendOrder }) => {
	const {
		getSelectedInactiveOption,
		changeItemsVisibility,
		setImportAction,
		openFacilityOrderTemplateItemNewFlowCalculationModal,
		productStore,
		saveItemOnProductSelect,
		editItem,
		updateItem,
		deleteFacilityOrderTemplateItem,
		setTemplateStatus,
		inactiveOptions,
		disabledItem,
		activateTemplate,
		reviewTemplate,
		form,
		missingDocumentStore,
		openFacilityOrderTemplateMissingDocumentNoteModal,
		setIncludeInDocReqEmailData,
		showDischargesDocReqEmailDataNotification,
		openPatientMissingDocsPreview,
		facilityOrder,
		isMasterPatient,
		missingDocsPreviousValue,
		isImportAllowed,
		importFacilityOrderTemplate,
		setItemToggle,
		insertImportActionId
	} = facilityOrderTemplateNewFlowClinicalEditViewStore;

	const productDropdownItemRender = (li, itemProps) => {
		const itemName = itemProps && itemProps.dataItem.description;
		const isPreferredIcon = itemProps && itemProps.dataItem.isPreferred && facilityOrderTemplate.itemCategory.abrv === "ostomy" ?
			<i className="u-icon u-icon--xsml u-icon--star-outlined u-mar--right--tny"></i> : null;
		const isBackorderedIcon = itemProps && itemProps.dataItem.backordered ? <i className="u-icon u-icon--sml u-icon--backordered u-mar--left--tny"></i> : null;
		const nonBillableIcon = itemProps && !itemProps.dataItem.billableToMedicare ? <i className="u-icon u-icon--sml u-icon--non-billable u-mar--left--tny"></i> : null;
		const itemChildren = (
			<span style={{ display: "flex", flexDirection: "column", width: "100%", }}>
				<span style={{ flex: "1" }}> {li.props.children} </span>
				<span style={{ fontSize: "12px" }}> {isPreferredIcon} {itemName} {nonBillableIcon} {isBackorderedIcon}</span>
			</span>
		);
		return React.cloneElement(li, li.props, itemChildren);
	}

	const includeInDocReqEmailData = [
		{
			text: "Yes",
			value: true,
		},
		{
			text: "No",
			value: false,
		}
	];

	const facilityOrderUSIntakeStatus = facilityOrder && facilityOrder.facilityOrderStatuses && facilityOrder.facilityOrderStatuses.find(i => i.status.abrv === 'us-intake');

	const handleDropDownItemsOnClick = async (e) => {
		if (facilityOrderUSIntakeStatus && facilityOrderUSIntakeStatus.completedOn) {
			activateTemplate();
		}
		else {
			if (e.item.text === "No changes") {
				setTemplateStatus("no-change", inactiveOptions[3].id, true);
			}
			else if (e.item.text === "Change/Review") {
				setTemplateStatus("change", inactiveOptions[3].id, true);
			}
			else if (e.item.text === "Discontinued") {
				setTemplateStatus("discharge", inactiveOptions[3].id, true);
			}
		}
	}
	const dropdownRef = React.createRef();

	const tagRender = (tagData, li) => React.cloneElement(li, { children: <span key={uuid()} onClick={() => openFacilityOrderTemplateMissingDocumentNoteModal(tagData.data[0])}>{tagData.data[0].name}</span> });
	const showDischargesDocReqEmailDataNotificationFlag = showDischargesDocReqEmailDataNotification();
	return (
		<div className={`table--secondary__wrapper u-mar--bottom--med--condition ${getSelectedInactiveOption()} is-add-new-disabled`}>
			<ReactTooltip positionStrategy="fixed" id={"registerTemplateTip" + facilityOrderTemplate.id} place="bottom" />
			<table width="100%" className="table--secondary">
				<colgroup>
					<col width="400" />
					<col width="150" />
					<col width="80" />
					<col width="90" />
					<col width="" />
					<col width="70" />
				</colgroup>
				<thead>
					<tr>
						<th colSpan={"6"} className="type--left">
							<div className="u-flex--space-between">
								<label>{facilityOrderTemplate.subCategory.name.toUpperCase()}</label>
								<div className="u-flex--default u-align--h--right">
									<div>
										<div data-tooltip-content="This template can't be imported" data-tooltip-id={(disabled || disabledSendOrder || !facilityOrderTemplate.isClinicalReviewed || facilityOrderTemplate.importDetailStatus.abrv !== 'new') ? ("registerTemplateTip" + facilityOrderTemplate.id) : ""}>
											{isImportAllowed && <Button className="btn btn--icon--sml-med btn--light u-mar--right--xsml" icon="u-icon u-icon--sml u-icon--upload" onClick={() => importFacilityOrderTemplate()} disabled={disabled || !facilityOrderTemplate.isClinicalReviewed || facilityOrderTemplate.importDetailStatus.abrv !== 'new' || facilityOrderTemplate.inactiveReasonId === 17 || facilityOrderTemplate.inactiveReasonId === 18} data-tooltip-content={"Import to Brightree"} data-tooltip-id={"registerTemplateTip" + facilityOrderTemplate.id} />}
										</div>
									</div>
									{facilityOrderTemplate.inactiveReasonId == "58" &&
										<div className={`card--tertiary__status--card ${disabled || disabledSendOrder || disabledItem ? "is-disabled" : ""}`}>
											<div className={`status--sqare__holder ${facilityOrderTemplate.newFlowStatus.abrv === "no-change" ? "success" : facilityOrderTemplate.newFlowStatus.abrv === "change" ? "warning" : facilityOrderTemplate.newFlowStatus.abrv === "discharge" ? "danger" : ""}`}>
												<DropDownButton
													text={`${facilityOrderTemplate.newFlowStatus.abrv === "no-change" ? "NO CHANGES" : facilityOrderTemplate.newFlowStatus.abrv === "change" ? "CHANGES" : facilityOrderTemplate.newFlowStatus.abrv === "discharge" ? "DISCONTINUED" : ""}`}
													fillMode={"solid"}
													themeColor={"primary"}
													onItemClick={(e) => handleDropDownItemsOnClick(e)}
													buttonClass={`btn btn--base btn--ghost k-dropdown-button--icons status--sqare ${facilityOrderTemplate.newFlowStatus.abrv === "no-change" ? "success" : facilityOrderTemplate.newFlowStatus.abrv === "change" ? "warning" : facilityOrderTemplate.newFlowStatus.abrv === "discharge" ? "danger" : ""}`}
													disabled={disabled || disabledSendOrder || disabledItem}
													popupSettings={{
														animate: false,
														anchorAlign: { horizontal: "right", vertical: "bottom" },
														popupAlign: { horizontal: "right", vertical: "top" },
														popupClass: 'is-clean'
													}}
												>
													{facilityOrderTemplate.newFlowStatus.abrv !== "no-change" && <DropDownButtonItem iconClass="u-icon u-icon--xmed u-icon--success" text="No changes" />}
													{facilityOrderTemplate.newFlowStatus.abrv !== "change" && <DropDownButtonItem iconClass="u-icon u-icon--xmed u-icon--warning-triangle" text="Change/Review" />}
													{facilityOrderTemplate.newFlowStatus.abrv !== "discharge" && <DropDownButtonItem iconClass="u-icon u-icon--xmed u-icon--danger" text="Discontinued" />}
												</DropDownButton>
											</div>
										</div>
									}
									{(showDischargesDocReqEmailDataNotificationFlag || !facilityOrderTemplate.isClinicalReviewed) &&
										(
											facilityOrderTemplate.isClinicalReviewed ?
												<div className="status--sqare__wrapper u-mar--left--xsml" data-tooltip-content={"Unreview"} data-tooltip-id={disabled || disabledSendOrder ? ("registerTemplateTip" + facilityOrderTemplate.id) : null}>
													<Button className="btn btn--icon btn--icon--sml-med btn--primary" icon="u-icon u-icon--med u-icon--unreview" onClick={() => { reviewTemplate() }} disabled={disabled || disabledSendOrder || disabledItem} data-tooltip-content="Unreview" data-tooltip-id={"registerTemplateTip" + facilityOrderTemplate.id} />
												</div>
												:
												<div className="status--sqare__wrapper u-mar--left--xsml" data-tooltip-content={(showDischargesDocReqEmailDataNotificationFlag && facilityOrderTemplate.pastFacilityOrderTemplate && facilityOrderTemplate.pastFacilityOrderTemplate.includeInDocReqEmail == null) ? "\"Include the past order’s missing docs in DR email\" must be selected" : "Review"} data-tooltip-id={"registerTemplateTip" + facilityOrderTemplate.id}>
													<Button className="btn btn--icon btn--icon--sml-med btn--primary" icon="u-icon u-icon--med u-icon--check-list" onClick={() => { reviewTemplate() }} disabled={disabled || disabledSendOrder || disabledItem || (showDischargesDocReqEmailDataNotificationFlag && facilityOrderTemplate.pastFacilityOrderTemplate && facilityOrderTemplate.pastFacilityOrderTemplate.includeInDocReqEmail == null)} data-tooltip-content={"Review"} data-tooltip-id={"registerTemplateTip" + facilityOrderTemplate.id} />
												</div>
										)
									}
								</div>
							</div>
							<div className="modal__body u-mar--top--sml is-blue">
								{showDischargesDocReqEmailDataNotificationFlag &&
									<div className="row">
										<div className="form__group col col-lrg-12">
											<div className="message message--sml-med message--note">
												<div className="u-flex--default w--100">
													<i className="u-icon u-icon--med u-icon--warning-circle--peach u-mar--right--sml"></i>
													<div className="u-mar--right--tny u-flex--default u-flex--grow--1">
														{facilityOrderTemplate.pastFacilityOrderTemplate ? "Include the past order’s missing docs in DR email?" : "You may want to request documents for the past order, but the system cannot find the past template."}
														<BaasicButton
															className="btn btn--icon u-mar--left--sml"
															icon="u-icon u-icon--expand u-icon--sml"
															onlyIcon={true}
															onClick={() => openPatientMissingDocsPreview()}
															disabled={disabled || disabledSendOrder || disabledItem}
															label="Patient Missing Documents"
														/>
													</div>
													{facilityOrderTemplate.pastFacilityOrderTemplate &&
														<div className="u-align--h--right type--whitespace--nowrap">
															<ChipList
																className="btn__group--preselect"
																disabled={disabled || disabledSendOrder || disabledItem}
																selection="single"
																data={includeInDocReqEmailData}
																value={facilityOrderTemplate.pastFacilityOrderTemplate.includeInDocReqEmail}
																onChange={(e) => { setIncludeInDocReqEmailData(e) }}
															/>
														</div>
													}
												</div>
											</div>
										</div>
									</div>}
								<div className="row">
									<div className="form__group col col-lrg-12">
										<BaasicFieldDropdown field={form.$('facilityOrderTemplateMissingDocuments')} store={missingDocumentStore} multi={true} tagRender={tagRender} autoClose={true} disabled={true} showChangeFlag={facilityOrderTemplate && facilityOrderTemplate.templateId && missingDocsPreviousValue != null} previousValue={missingDocsPreviousValue != null && missingDocsPreviousValue} />
									</div>
								</div>
							</div>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td colSpan={6} className="table--secondary__last card--tertiary__body u-cursor--pointer" onClick={() => { changeItemsVisibility() }}>
							<div className="">
								<div className="u-flex u-flex--default">
									<span className="type--tiny type--color--opaque"> {facilityOrderTemplate.itemsVisible ? "Hide Items" : "See Items"}</span>
									<i className={"u-icon u-icon--xsml " + (facilityOrderTemplate.itemsVisible ? "u-icon--arrow-up" : "u-icon--arrow-down") + " u-mar--left--sml"} />
								</div>
							</div>
						</td>
					</tr>
					{facilityOrderTemplate.itemsVisible && facilityOrderTemplate.facilityOrderTemplateItems.map((item, index) => {
						if (!item.product && item.toggle) {
							setTimeout(() => {
								if (dropdownRef.current) {
									// v14 dropped the inner .k-dropdown-wrap — the DropDownList root element is the clickable toggle
									const toggle = dropdownRef.current.element;
									if (toggle) {
										setItemToggle(item);
										toggle.click();
									}
								}
							}, 0);
						}
						return (
							<tr key={item.id} className={`${(facilityOrderTemplate.importActionId != insertImportActionId && item.importActionId === insertImportActionId) ? "new-row" : (item.importAction && item.importAction.abrv === "delete" ? "deleted-row" : "")} ${!item.product ? "is-focused" : ""}`}>
								<td className="is-flex">
									{
										item.product ? <React.Fragment>{item.product.description} <span className="table__icons-wrapper u-mar--left--auto"> {!item.product.billableToMedicare && <i className="u-icon u-icon--sml u-icon--non-billable"></i>} {(item.backordered != null ? item.backordered : item.product.backordered) && <i className="u-icon u-icon--med-lrg u-icon--backordered"></i>}</span></React.Fragment> :
											<BaasicDropdown
												placeholder="Select item name..."
												store={productStore}
												value={item.product}
												className="input--table"
												itemRender={productDropdownItemRender}
												onChange={(e) => { saveItemOnProductSelect(item, index, e.target.value) }}
												disabled={disabled || disabledSendOrder || disabledItem}
												customRef={dropdownRef}
											/>
									}
								</td>
								<td>
									{item.product && item.product.itemId}
								</td>
								<td className="table-col-input text-align-left">
									{item.procedureCode && item.procedureCode}
								</td>
								<td className="table-col-input text-align-left">
									<ReactTooltip positionStrategy="fixed" id={"registerTipItemChanged" + item.id} place="top" />
									{item.isTemplateQuantityChanged && <div className="table-col-input__notif" data-tooltip-id={"registerTipItemChanged" + item.id} data-tooltip-content={"Previous: " + (item.facilityOrderTemplateItemInitial && item.facilityOrderTemplateItemInitial.templateQuantity ? item.facilityOrderTemplateItemInitial.templateQuantity : "blank")}></div>}
									{item.product &&
										<input
											className="input--table input--inherit-color no-arrows input--text-align--left w--70--px"
											onKeyDown={handleKeyDown}
											onPaste={handlePaste}
											onClick={(e) => e.target.select()}
											onChange={(e) => { editItem(item, parseInt(e.target.value), 'templateQuantity') }}
											onBlur={() => { updateItem(item) }}
											value={item.templateQuantity}
											type="number"
											min="0"
											disabled={disabled || disabledSendOrder || disabledItem || isMasterPatient}
										/>
									}
									{(item.sendSeparatelyQty | 0) > 0 && <i className="u-icon u-icon--ss u-icon--sml table__icon--ss" data-tooltip-id={"registerTipItemChanged" + item.id} data-tooltip-content={item.sendSeparatelyQty} />}
								</td>
								<td className="table-col-input">
									<ReactTooltip positionStrategy="fixed" id={"registerTipItemChangedNote" + item.id} place="top" />
									{item.isNoteChanged && <div className="table-col-input__notif" data-tooltip-id={"registerTipItemChangedNote" + item.id} data-tooltip-content={"Previous: " + (item.facilityOrderTemplateItemInitial && item.facilityOrderTemplateItemInitial.note ? item.facilityOrderTemplateItemInitial.note : "blank")}></div>}
									{!isMasterPatient && item.product && item.product.itemType && (item.product.itemType.abrv === "can" || item.product.itemType.abrv === "bottle") ?
										<input
											className="input--table input--inherit-color no-arrows input--text-align--left"
											onClick={() => openFacilityOrderTemplateItemNewFlowCalculationModal(facilityOrderTemplate.facilityOrderTemplateItems, index)}
											value={item.note}
											defaultValue={item.note}
											disabled={disabled || disabledSendOrder || disabledItem}
										/> :
										<input
											className="input--table input--inherit-color no-arrows input--text-align--left"
											onClick={(e) => e.target.select()}
											onChange={(e) => { item.note = e.target.value }}
											onBlur={() => { updateItem(item, true) }}
											value={item.note}
											disabled={disabled || disabledSendOrder || disabledItem}
										/>
									}
								</td>
								<td className="type--center is-hidden">
									<div className="table__icons">
										{item.importAction && item.importAction.abrv === "delete" ?
											<Button
												className="btn btn--icon"
												icon="u-icon u-icon--sml u-icon--undo--table"
												label="Undelete"
												onlyIcon={true}
												onClick={() => setImportAction(item, "update", true)}
												disabled={disabled || disabledSendOrder || disabledItem}
												title={"Undo"}
											></Button>
											:
											<Button
												className="btn btn--icon is-delete"
												icon={`u-icon u-icon--sml ${!item.product ? "u-icon--close--table" : "u-icon--delete--primary"}`}
												label="Delete"
												onlyIcon={true}
												onClick={() => { deleteFacilityOrderTemplateItem(index, true) }}
												disabled={disabled || disabledSendOrder || disabledItem}
												title={!item.product ? "Cancel" : "Delete"}
											></Button>
										}
									</div>
								</td>
							</tr>
						);
					})
					}
				</tbody>
			</table>
		</div>
	);
});

FacilityOrderTemplateNewFlowClinicalEditTemplate.propTypes = {
	facilityOrderTemplateNewFlowClinicalEditViewStore: PropTypes.object.isRequired,
	t: PropTypes.func,
	disabled: PropTypes.bool,
	disabledSendOrder: PropTypes.bool
};

FacilityOrderTemplateTable.propTypes = {
	facilityOrderTemplate: PropTypes.object.isRequired,
	facilityOrderTemplateNewFlowClinicalEditViewStore: PropTypes.object.isRequired,
	disabled: PropTypes.bool,
	disabledSendOrder: PropTypes.bool,
	t: PropTypes.func
};

FacilityOrderTemplateDischargesTable.propTypes = {
	facilityOrderTemplate: PropTypes.object.isRequired,
	facilityOrderTemplateNewFlowClinicalEditViewStore: PropTypes.object.isRequired,
	disabled: PropTypes.bool,
	disabledSendOrder: PropTypes.bool
};

export default defaultTemplate(FacilityOrderTemplateNewFlowClinicalEditTemplate);